import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import ThemeToggle from "./ThemeToggle";
import Button from "./Button";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass glass-light"
          : "bg-transparent"
      }`}
    >
      <div className="container flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-2 group"
        >
          <span className="grid place-items-center w-9 h-9 rounded-sm bg-primary text-primary-foreground font-display font-black text-lg leading-none">
            F
          </span>
          <div className="leading-tight">
            <div className="font-display font-bold tracking-tight text-lg">
              FRADRO
            </div>
            <div className="overline text-muted-foreground -mt-0.5">
              Group
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-primary"
                    : "text-foreground/80 hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className={`pointer-events-none absolute left-4 right-4 -bottom-0.5 h-px transition-all ${
                      isActive ? "bg-primary opacity-100" : "bg-primary opacity-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Button
            to="/contact"
            size="sm"
            variant="primary"
            iconRight={ArrowUpRight}
            className="hidden md:inline-flex"
            data-testid="header-cta"
          >
            Get in touch
          </Button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-border/70"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container pb-6 pt-2 flex flex-col gap-1 border-t border-border/60">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center justify-between py-3 px-2 border-b border-border/40 ${
                  isActive ? "text-primary" : "text-foreground/85"
                }`
              }
            >
              <span className="font-display text-xl">{n.label}</span>
              <ArrowUpRight size={18} />
            </NavLink>
          ))}
          <Button to="/contact" className="mt-4" data-testid="mobile-cta">
            Get in touch
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;
